import { z } from 'zod';
import {
  addressSchema,
  emailSchema,
  phoneSchema,
  requiredBoolean,
  futureDateSchema,
  photoArraySchema,
} from '@/shared/validation/common.schemas';

/**
 * Validation schema for waste collection form
 *
 * Uses Context7-validated patterns:
 * - z.coerce.number() for automatic string→number conversion
 * - futureDateSchema() for date validation (minimum 15 days from now)
 * - photoArraySchema() for photo array validation (min 2, max 10)
 * - Reuses common schemas (addressSchema, emailSchema, phoneSchema)
 */

// Helper: Positive number or empty (for waste quantities)
const positiveNumberOrEmpty = z.coerce.number().min(0, 'Valoarea trebuie să fie pozitivă').optional();

/**
 * Main waste collection form schema
 * Satisfies WasteCollectionFormDto type for type safety
 */
export const wasteCollectionSchema = z.object({
  // Contact Information (Required)
  contactPersonName: z.string().min(1, 'Numele persoanei de contact este obligatoriu'),
  contactPersonEmail: emailSchema,
  contactPersonPhone: phoneSchema,

  // Pickup Date (Required, editable via DatePicker, minimum 15 days from now)
  dataOrientativaColectare: futureDateSchema(15),

  // Address Toggle (Required)
  pickupAddressSameWithSchoolAddress: z.boolean({
    required_error: 'Vă rugăm să specificați dacă adresa de colectare este aceeași cu adresa școlii',
  }),

  // Pickup Address (Required, nested object)
  pickupAddress: addressSchema,

  // Waste Quantities (Optional, automatic number coercion)
  deeeKg: positiveNumberOrEmpty,
  dbaKg: positiveNumberOrEmpty,
  becuriSiNeoaneKg: positiveNumberOrEmpty,

  // Photos (Required, min 2, max 10)
  photos: photoArraySchema(2, 10),

  // Consent (Required)
  consent: requiredBoolean('Trebuie să fiți de acord cu prelucrarea datelor'),
});

/**
 * Inferred TypeScript type from schema
 */
export type WasteCollectionFormData = z.infer<typeof wasteCollectionSchema>;

/**
 * Helper function to generate minimum pickup date (15 days from now)
 */
export function generateMinPickupDate(): Date {
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 15);
  minDate.setHours(0, 0, 0, 0);
  return minDate;
}

/**
 * Helper function to generate default pickup date (15 days from now)
 */
export function generateDefaultPickupDate(): Date {
  return generateMinPickupDate();
}
